import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Outlet,
  Route,
  Routes,
  useBlocker,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router';
import {
  PACKAGE_STATE_LABELS,
  PROSPECT_TEXTS,
  assetMeta,
  isPersonalDomain,
  packageState,
  type PackageRequest,
} from '@wr/shared';
import { ApiError, del, downloadFile, errorMessage, get, patch, post } from '../../api.js';
import { Icon } from '../../components/Icon.js';
import type { LinkItem, SearchResult } from '../../components/LinkPicker.js';
import { useToast } from '../../components/Toast.js';
import {
  Dialog,
  EmptyState,
  Field,
  Notice,
  PageHead,
  SelectField,
  TextAreaField,
} from '../../components/ui.js';
import { LISTS } from '../../lists.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import { shortDate } from '../leads/LeadSheet.js';
import { Thumb, type Asset } from '../content/ContentScreen.js';

interface Row {
  id: string;
  title: string;
  company_name: string;
  recipient_name: string;
  link_kind: 'lead' | 'deal' | 'contact';
  link_label: string;
  expires_on: string | null;
  created_at: string;
  created_by: string;
  created_by_name: string | null;
  opens: number;
  last_open: string | null;
}

interface Detail extends Row {
  message: string | null;
  lang: 'en' | 'fr';
  url: string;
  recipient_email: string;
  recipient_contact_id: string | null;
  access: { domain?: string | null; email?: string | null };
  expired: boolean;
  roi_project_id: string | null;
  asset_ids: string[];
  items: { id: string; format: string; kind: string; title: string }[];
  roi: { id: string; scenario_name: string; calculator: string } | null;
  stats: {
    opens: number;
    last_open: string | null;
    views: number;
    watches: number;
    downloads: number;
  };
  people: { email: string; visits: number; last: string | null }[];
  looked: { asset_id: string | null; kind: string; people: number; times: number }[];
  requests: {
    id: string;
    kind: PackageRequest;
    message: string | null;
    at: string;
    email: string;
  }[];
}

interface Recipient {
  source: 'portal' | 'hubspot';
  id: string;
  name: string;
  email: string;
  company: string | null;
}

const LANGUAGES: Record<string, string> = Object.fromEntries(
  LISTS.contentLanguages.map((l) => [l.id, l.label]),
);
const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);
const LINK_KIND = { lead: 'Lead', deal: 'Deal', contact: 'Contact' } as const;
const BADGE = { active: 'b-ok', expires_soon: 'b-soon', expired: 'b-off' } as const;

/** "3 h ago", "10 days ago". */
export function ago(iso: string | null): string {
  if (!iso) return '';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `${Math.max(1, minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * Packages (brief section 7.6, docs/prototype.html), mounted at
 * /packages/*: the list, New package (Send to a contact with an email;
 * the link to its lead, deal or contact is found by itself), the package
 * with its page's link and its stats (the activity itself goes to HubSpot).
 */
export function PackagesScreen() {
  return (
    <Routes>
      <Route element={<PackageList />}>
        <Route index element={null} />
        <Route path="new" element={<PackageForm />} />
        <Route path=":id" element={<PackageWindow />} />
        <Route path=":id/edit" element={<PackageForm />} />
      </Route>
    </Routes>
  );
}

function PackageList() {
  const { can, refresh } = useSignedIn();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<{ items: Row[]; total: number; pageSize: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [search, setSearch] = useState(params.get('q') ?? '');
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? 'all';
  const owner = params.get('owner') ?? 'all';
  const page = Number(params.get('page') ?? '1') || 1;

  // A window opened meanwhile wins: its address may not be rendered yet, so
  // the search waits for the next render.
  useEffect(() => {
    if (search === q) return;
    const timer = setTimeout(() => {
      if (window.location.pathname === location.pathname) set('q', search);
    }, 300);
    return () => clearTimeout(timer);
  });

  useEffect(() => {
    let current = true;
    const query = new URLSearchParams({ q, status, owner, page: String(page) });
    get<{ items: Row[]; total: number; pageSize: number }>(`/packages?${query}`)
      .then((d) => {
        if (!current) return;
        setError(null);
        setData(d);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [q, status, owner, page, tick, refresh]);

  function set(name: string, value: string, empty = '') {
    const next = new URLSearchParams(params);
    if (value === empty) next.delete(name);
    else next.set(name, value);
    if (name !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  return (
    <>
      <PageHead
        title="Packages"
        actions={
          can('packages.create') && (
            <button type="button" className="btn sm primary" onClick={() => navigate('new')}>
              New package
            </button>
          )
        }
        lede="One private link per prospect with the videos, PDFs and ROI you picked. Prospect activity goes to HubSpot."
      />
      <div className="toolbar">
        <input
          className="grow"
          type="search"
          placeholder="Search title, company, contact, linked record"
          aria-label="Search packages"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label="Status"
          value={status}
          onChange={(e) => set('status', e.target.value, 'all')}
        >
          <option value="all">Any status</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
        </select>
        {can('packages.read.all') && (
          <select
            aria-label="Author"
            value={owner}
            onChange={(e) => set('owner', e.target.value, 'all')}
          >
            <option value="all">Everyone</option>
            <option value="mine">Only mine</option>
          </select>
        )}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {!data && !error && <div className="spinner" aria-label="Loading" />}
      {data && (
        <p className="note pk-count">
          {data.total} package{data.total === 1 ? '' : 's'}
          {pages > 1 ? `, page ${page} of ${pages}` : ''}
        </p>
      )}
      {data && data.items.length > 0 && (
        <div className="list" aria-label="Packages">
          {data.items.map((p) => {
            const state = packageState(p.expires_on, today());
            return (
              <button key={p.id} type="button" className="row" onClick={() => navigate(p.id)}>
                <span className="av lead">
                  <Icon name="packages" />
                </span>
                <span className="tx">
                  <span className="t1">{p.title || p.company_name}</span>
                  <span className="t2">
                    {[p.company_name, p.recipient_name].filter(Boolean).join(', ')}
                  </span>
                  <span className="le-tags">
                    <span className="tag">
                      {p.link_label} ({LINK_KIND[p.link_kind]})
                    </span>
                    <span className="tag">Created {shortDate(p.created_at)}</span>
                    {p.expires_on && <span className="tag">Expires {shortDate(p.expires_on)}</span>}
                  </span>
                  <span className="t2 pk-act">
                    {p.opens
                      ? `Opened ${p.opens} time${p.opens === 1 ? '' : 's'}, last ${ago(p.last_open)}`
                      : 'Not opened yet'}
                  </span>
                </span>
                <span className="rt">
                  <span className={`badge ${BADGE[state]}`}>{PACKAGE_STATE_LABELS[state]}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {data && data.items.length === 0 && (
        <EmptyState title="No package found.">
          {q || status !== 'all' || owner !== 'all'
            ? 'Change the search or the filters.'
            : 'Create one from New package or from the content library.'}
        </EmptyState>
      )}
      {pages > 1 && (
        <div className="pager">
          <button
            type="button"
            className="btn sm"
            disabled={page <= 1}
            onClick={() => set('page', String(page - 1), '1')}
          >
            Previous
          </button>
          <span>
            {page} / {pages}
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={page >= pages}
            onClick={() => set('page', String(page + 1), '1')}
          >
            Next
          </button>
        </div>
      )}
      <Outlet context={{ reload: () => setTick((t) => t + 1) }} />
    </>
  );
}

function useDetail(id: string | undefined) {
  const { refresh } = useSignedIn();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!id) return;
    let current = true;
    get<Detail>(`/packages/${encodeURIComponent(id)}`)
      .then((d) => current && setDetail(d))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) {
          setError(
            err instanceof ApiError && err.status === 404
              ? 'This package does not exist, or it was deleted.'
              : errorMessage(err),
          );
        }
      });
    return () => {
      current = false;
    };
  }, [id, refresh]);
  return { detail, error };
}

/** The package (prototype: openShare): its link, who may open it, requests and activity. */
function PackageWindow() {
  const { id } = useParams();
  const { user } = useSignedIn();
  const { reload } = useOutletContext<{ reload(): void }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { detail: p, error } = useDetail(id);
  const [confirm, setConfirm] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const close = () => navigate('/packages', { replace: true });
  if (!p) {
    return (
      <Dialog title="Package" onClose={close}>
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </Dialog>
    );
  }
  const mine = p.created_by === user.id;
  const state = packageState(p.expires_on, today());
  const deal = p.link_kind === 'deal' || p.link_kind === 'lead';
  const people = p.people.length;
  const lookedAt = (assetId: string | null, kinds: string[]) =>
    p.looked.filter((l) => l.asset_id === assetId && kinds.includes(l.kind));

  async function remove() {
    try {
      await del(`/packages/${p!.id}`);
      toast('Package deleted. Its page no longer opens.');
      reload();
      close();
    } catch (err) {
      setFailed(errorMessage(err));
    }
  }

  return (
    <Dialog
      title={p.company_name}
      className="wide"
      onClose={close}
      footer={
        <>
          {mine && (
            <>
              <button type="button" className="btn danger left" onClick={() => setConfirm(true)}>
                Delete
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => navigate('edit', { replace: true })}
              >
                Edit
              </button>
            </>
          )}
          {p.roi && (
            <button
              type="button"
              className="btn"
              onClick={() =>
                void downloadFile(`/roi/projects/${p.roi!.id}/pdf`).catch((err: unknown) =>
                  setFailed(errorMessage(err)),
                )
              }
            >
              ROI PDF
            </button>
          )}
          <button type="button" className="btn primary" onClick={close}>
            Done
          </button>
        </>
      }
    >
      <div className="hs-state ok">
        <b>Activity is sent to HubSpot</b>
        <span>
          Opens, views, downloads and forwards go to the timeline of {p.recipient_name}
          {deal ? ' and its deal' : ''}. Each request creates a HubSpot task for you, and "Last
          package activity" is updated.
        </span>
      </div>
      <div>
        <div className="pk-title">{p.title}</div>
        <div className="actions pk-meta">
          <span className="note">
            For {p.recipient_name}, {p.items.length} item(s){p.roi ? ' and an ROI' : ''}
            {p.expires_on ? `, expires ${shortDate(p.expires_on)}` : ', no expiry'}
          </span>
          <span className={`badge ${BADGE[state]}`}>{PACKAGE_STATE_LABELS[state]}</span>
        </div>
      </div>
      <div className="sharebox">
        <input
          type="text"
          readOnly
          aria-label="Share link"
          value={p.url}
          onFocus={(e) => e.target.select()}
        />
        <button
          type="button"
          className="btn primary"
          onClick={() =>
            void navigator.clipboard
              ?.writeText(p.url)
              .then(() => toast('Link copied.'))
              .catch(() => toast('Select the link and copy it.'))
          }
        >
          Copy
        </button>
      </div>
      <p className="note pk-access">
        {p.access.email ? (
          <>
            Opens only with <b>{p.access.email}</b> (a personal address).
          </>
        ) : (
          <>
            Opens with a work email from <b>@{p.access.domain}</b>.
          </>
        )}{' '}
        PDFs downloadable, videos on the page only.
      </p>
      <button
        type="button"
        className="btn block"
        onClick={() => window.open(p.url, '_blank', 'noopener')}
      >
        Show the prospect page
      </button>
      {p.requests.length > 0 && (
        <div className="formsec reqs">
          <h3>Requests from the prospect</h3>
          {p.requests.map((r) => (
            <div key={r.id} className="req">
              <span className="req-ic">
                <Icon name={r.kind === 'call' ? 'phone' : r.kind === 'question' ? 'mail' : 'roi'} />
              </span>
              <span>
                <b>
                  {r.email}: {PROSPECT_TEXTS.en.requests[r.kind][0]}
                </b>
                <span className="note">
                  {ago(r.at)}
                  {r.message ? `: ${r.message}` : ''}
                </span>
              </span>
            </div>
          ))}
          <p className="note pk-reqs-note">
            Each request creates a HubSpot task for you{deal ? ' on the deal' : ''}.
          </p>
        </div>
      )}
      <div className="formsec">
        <h3>Activity</h3>
        <div className="kpis">
          <div className="kpi">
            <b>{p.stats.opens}</b>
            <span>Opens</span>
          </div>
          <div className="kpi">
            <b>{people}</b>
            <span>People</span>
          </div>
          <div className="kpi">
            <b>{p.stats.downloads}</b>
            <span>Downloads</span>
          </div>
          <div className="kpi">
            <b>{p.stats.last_open ? ago(p.stats.last_open).replace(' ago', '') : 'None'}</b>
            <span>Since last open</span>
          </div>
        </div>
        {p.people.some((x) => x.email !== p.recipient_email) && (
          <div className="dup">
            <b>Forwarded inside the account</b>
            <span>
              Someone other than {p.recipient_name} opened the page, so the package is circulating
              internally. A good moment to call.
            </span>
          </div>
        )}
      </div>
      {people > 0 && (
        <div className="formsec">
          <h3>Who opened it</h3>
          <div className="list pk-people">
            {p.people.map((x) => (
              <div key={x.email} className="row pk-person">
                <span className="av">{x.email.slice(0, 2).toUpperCase()}</span>
                <span className="tx">
                  <span className="t1">
                    {x.email}
                    {x.email !== p.recipient_email && (
                      <span className="badge b-warm">Forwarded</span>
                    )}
                  </span>
                </span>
                <span className="rt">
                  <b>
                    {x.visits} visit{x.visits === 1 ? '' : 's'}
                  </b>
                  <span>{ago(x.last)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {people > 0 && (
        <div className="formsec">
          <h3>What they looked at</h3>
          {p.roi && (
            <Looked
              label={`ROI: ${p.roi.scenario_name}`}
              stats={lookedAt(null, ['roi_download'])}
              of={people}
            />
          )}
          {p.items.map((i) => (
            <Looked
              key={i.id}
              label={`${i.format === 'video' ? 'Video' : 'PDF'}: ${i.title}`}
              stats={lookedAt(i.id, ['view', 'watch', 'download'])}
              of={people}
            />
          ))}
        </div>
      )}
      {failed && <Notice kind="error">{failed}</Notice>}
      {confirm && (
        <Dialog
          small
          title="Delete this package?"
          onClose={() => setConfirm(false)}
          footer={
            <>
              <button type="button" className="btn" autoFocus onClick={() => setConfirm(false)}>
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={() => void remove()}>
                Delete
              </button>
            </>
          }
        >
          <p className="note">
            Its page stops opening at once. HubSpot keeps the activity already sent.
          </p>
        </Dialog>
      )}
    </Dialog>
  );
}

function Looked({
  label,
  stats,
  of,
}: {
  label: string;
  stats: { kind: string; people: number; times: number }[];
  of: number;
}) {
  const people = Math.max(0, ...stats.map((s) => s.people));
  const downloads = stats.filter((s) => s.kind === 'download' || s.kind === 'roi_download');
  const n = downloads.reduce((sum, s) => sum + s.times, 0);
  return (
    <div className="istat">
      <div className="istat-h">
        <span>{label}</span>
        <b>
          {people} of {of}
        </b>
      </div>
      <span className="htrack">
        <i style={{ width: `${of ? (people / of) * 100 : 0}%` }} />
      </span>
      <span className="note">
        {people} {people === 1 ? 'person' : 'people'}
        {n ? `, ${n} download${n === 1 ? '' : 's'}` : ''}
      </span>
    </div>
  );
}

interface Draft {
  company: string;
  recipient: Recipient | null;
  title: string;
  message: string;
  lang: 'en' | 'fr';
  expires: string;
  roi: string;
  assets: string[];
}

/** New package and Edit (prototype: openPackageForm). */
function PackageForm() {
  const { id } = useParams();
  const { can, user } = useSignedIn();
  const location = useLocation();
  const { reload } = useOutletContext<{ reload(): void }>();
  const navigate = useNavigate();
  const toast = useToast();
  const formId = useId();
  const editing = Boolean(id);
  const { detail, error: loadError } = useDetail(id);
  const preset = (location.state as { assets?: string[] } | null)?.assets ?? [];
  const close = () => navigate(id ? `/packages/${id}` : '/packages', { replace: true });

  if (!can('packages.create') || (detail && detail.created_by !== user.id)) {
    return (
      <Dialog small title="Not allowed" onClose={close}>
        <p className="note">Only its author changes a package.</p>
      </Dialog>
    );
  }
  if (editing && !detail) {
    return (
      <Dialog title="Edit package" onClose={close}>
        {loadError ? (
          <Notice kind="error">{loadError}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </Dialog>
    );
  }
  return (
    <PackageFields
      key={detail?.id ?? 'new'}
      detail={detail}
      preset={preset}
      formId={formId}
      onClose={close}
      onSaved={(saved, created) => {
        reload();
        toast(created ? 'Package created. Copy its link and send it.' : 'Package saved.');
        navigate(`/packages/${saved}`, { replace: true });
      }}
    />
  );
}

function PackageFields({
  detail,
  preset,
  formId,
  onClose,
  onSaved,
}: {
  detail: Detail | null;
  preset: string[];
  formId: string;
  onClose(): void;
  onSaved(id: string, created: boolean): void;
}) {
  const { can } = useSignedIn();
  const initial = useMemo<Draft>(
    () => ({
      company: detail?.company_name ?? '',
      recipient: detail
        ? {
            source: 'portal',
            id: detail.recipient_contact_id ?? '',
            name: detail.recipient_name,
            email: detail.recipient_email,
            company: detail.company_name,
          }
        : null,
      title: detail?.title ?? '',
      message: detail?.message ?? '',
      lang: detail?.lang ?? 'en',
      expires: detail ? (detail.expires_on ?? '') : inDays(30),
      roi: detail?.roi_project_id ?? '',
      assets: detail?.asset_ids ?? preset,
    }),
    [detail, preset],
  );
  const [draft, setDraft] = useState<Draft>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [library, setLibrary] = useState<Asset[]>([]);
  const [rois, setRois] = useState<
    { id: string; scenario_name: string; link_label: string; link_company: string }[]
  >([]);
  const [filter, setFilter] = useState('');
  const done = useRef(false);
  // The company filled from Send to (round 2): a later recipient replaces it,
  // never a company the person typed or picked.
  const companyAuto = useRef(false);
  async function fillCompany(r: Recipient) {
    let company = r.company?.trim() || null;
    if (!company && r.id) {
      company = await get<{ company: string | null }>(
        `/packages/recipients/company?source=${r.source}&id=${encodeURIComponent(r.id)}`,
      ).then(
        (a) => a.company?.trim() || null,
        () => null,
      );
    }
    if (!company) return;
    const found = company;
    setDraft((d) => {
      if (d.company.trim() && !companyAuto.current) return d;
      companyAuto.current = true;
      return { ...d, company: found };
    });
    setErrors((e) => ({ ...e, company: undefined }));
  }
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !done.current && currentLocation.pathname !== nextLocation.pathname,
  );
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  useEffect(() => {
    if (!can('content.read')) return;
    let current = true;
    get<{ items: Asset[] }>('/content')
      .then(({ items }) => current && setLibrary(items))
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [can]);

  // Attach an ROI: the company's projects, the newest proposed (brief section 7.6).
  const company = draft.company.trim();
  useEffect(() => {
    if (!can('roi.use')) return;
    let current = true;
    const timer = setTimeout(() => {
      get<{ items: typeof rois }>(`/packages/rois?company=${encodeURIComponent(company)}`)
        .then(({ items }) => {
          if (!current) return;
          setRois(items);
          const proposed = items.find(
            (r) => r.link_company.toLowerCase() === company.toLowerCase(),
          );
          setDraft((d) => (d.roi || !proposed || detail ? d : { ...d, roi: proposed.id }));
        })
        .catch(() => undefined);
    }, 300);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [company, can, detail]);

  const shown = library.filter((a) =>
    `${a.title} ${a.products.join(' ')} ${a.tags.join(' ')}`
      .toLowerCase()
      .includes(filter.trim().toLowerCase()),
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problems: Partial<Record<keyof Draft, string>> = {};
    if (!draft.company.trim()) problems.company = 'Prospect company is required.';
    if (!draft.recipient) problems.recipient = 'Choose the contact the package is sent to.';
    if (draft.assets.length === 0 && !draft.roi)
      problems.assets = 'Pick at least one video, PDF or an ROI.';
    setErrors(problems);
    if (Object.keys(problems).length > 0 || busy) return;
    setBusy(true);
    setFailed(null);
    const fields = {
      title: draft.title.trim() || `${draft.company.trim()}: your Waste Robotics information`,
      message: draft.message.trim() || null,
      lang: draft.lang,
      expires_on: draft.expires || null,
      roi_project_id: draft.roi || null,
      asset_ids: draft.assets,
    };
    try {
      if (detail) {
        await patch(`/packages/${detail.id}`, fields);
        done.current = true;
        onSaved(detail.id, false);
      } else {
        const id = newId();
        await post('/packages', {
          id,
          company_name: draft.company.trim(),
          recipient: { source: draft.recipient!.source, id: draft.recipient!.id },
          ...fields,
        });
        done.current = true;
        onSaved(id, true);
      }
    } catch (err) {
      setFailed(
        err instanceof ApiError && err.status === 503
          ? 'HubSpot does not answer: choose a contact of the portal, or try again in a moment.'
          : errorMessage(err),
      );
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={detail ? 'Edit package' : 'New package'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary" disabled={busy}>
            {detail ? 'Save changes' : 'Create package'}
          </button>
        </>
      }
    >
      <form id={formId} className="dlg-form" noValidate onSubmit={(e) => void submit(e)}>
        <div className="g2">
          {detail ? (
            <Field label="Prospect company" mark value={draft.company} readOnly />
          ) : (
            <CompanyField
              value={draft.company}
              error={errors.company}
              onChange={(company) => {
                companyAuto.current = false;
                set('company', company);
              }}
            />
          )}
        </div>
        <SendTo
          value={draft.recipient}
          fixed={Boolean(detail)}
          error={errors.recipient}
          onChange={(r) => {
            set('recipient', r);
            if (r) void fillCompany(r);
          }}
        />
        <Field
          label="Page title"
          hint="Shown to the prospect"
          placeholder="Your robotic sorting system"
          value={draft.title}
          onChange={(e) => set('title', e.target.value)}
        />
        <TextAreaField
          label="Message"
          placeholder="Great meeting you at the booth. Here is what we discussed."
          value={draft.message}
          onChange={(e) => set('message', e.target.value)}
        />
        <div className="g2">
          <SelectField
            label="Page language"
            value={draft.lang}
            onChange={(e) => set('lang', e.target.value as 'en' | 'fr')}
          >
            <option value="en">English</option>
            <option value="fr">French</option>
          </SelectField>
          <Field
            label="Expires on"
            hint="Empty for no expiry"
            type="date"
            min={today()}
            value={draft.expires}
            onChange={(e) => set('expires', e.target.value)}
          />
        </div>
        <div className="formsec">
          <h3>Access</h3>
          <p className="wrap-intro">
            The visitor enters an email to open the page. Accepted: any email from the recipient's
            company domain, so colleagues can open a forwarded link; or, if the recipient uses a
            personal address (Gmail, Outlook…), only that exact email.
          </p>
          <p className="note wrap-intro">
            PDFs and the ROI can be downloaded; videos play on the page only. Package pages are
            never indexed and their link cannot be guessed.
          </p>
        </div>
        {can('roi.use') && (
          <SelectField
            label="Attach an ROI"
            value={draft.roi}
            onChange={(e) => set('roi', e.target.value)}
          >
            <option value="">No ROI</option>
            {rois.map((r) => (
              <option key={r.id} value={r.id}>
                {r.link_company}: {r.scenario_name}
              </option>
            ))}
          </SelectField>
        )}
        <fieldset aria-invalid={errors.assets ? true : undefined}>
          <legend>Videos and PDFs ({draft.assets.length} selected)</legend>
          <input
            type="search"
            className="pk-filter"
            placeholder="Filter"
            aria-label="Filter content"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="picker" aria-label="Videos and PDFs">
            {shown.map((a) => (
              <label key={a.id}>
                <input
                  type="checkbox"
                  checked={draft.assets.includes(a.id)}
                  onChange={(e) =>
                    set(
                      'assets',
                      e.target.checked
                        ? [...draft.assets, a.id]
                        : draft.assets.filter((x) => x !== a.id),
                    )
                  }
                />
                <span className="mini">
                  <Thumb asset={a} />
                </span>
                <span>
                  <span className="pk-item-title">{a.title}</span>
                  <br />
                  <span className="meta">{assetMeta(a, LANGUAGES)}</span>
                </span>
              </label>
            ))}
            {library.length === 0 && <div className="lp-msg">The content library is empty.</div>}
          </div>
          {errors.assets && <small className="err">{errors.assets}</small>}
        </fieldset>
        {failed && <Notice kind="error">{failed}</Notice>}
      </form>
      {blocker.state === 'blocked' && (
        <Dialog
          small
          title="Close without saving?"
          onClose={() => blocker.reset?.()}
          footer={
            <>
              <button type="button" className="btn" autoFocus onClick={() => blocker.reset?.()}>
                Keep editing
              </button>
              <button type="button" className="btn primary" onClick={() => blocker.proceed?.()}>
                Close without saving
              </button>
            </>
          }
        >
          <p className="note">This package is not saved.</p>
        </Dialog>
      )}
    </Dialog>
  );
}

/**
 * Prospect company (round 2): typed, or picked among the portal's and
 * HubSpot's companies as Send to finds its contacts (from 2 letters).
 */
function CompanyField({
  value,
  error,
  onChange,
}: {
  value: string;
  error?: string;
  onChange(company: string): void;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<LinkItem[] | null>(null);
  const [hubspotDown, setHubspotDown] = useState(false);
  const q = value.trim();

  useEffect(() => {
    if (!open || q.length < 2) return;
    let current = true;
    const timer = setTimeout(() => {
      get<SearchResult>(`/people/search?q=${encodeURIComponent(q)}&kinds=company&limit=8`)
        .then((r) => {
          if (!current) return;
          // One line per company name, the portal's first.
          const seen = new Set<string>();
          setItems(
            r.items.filter((c) => {
              const key = c.label.trim().toLowerCase();
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            }),
          );
          setHubspotDown(r.hubspot === 'unavailable');
        })
        .catch(() => current && setItems([]));
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [q, open]);

  const shown = open && q.length >= 2 && items;
  return (
    <div className="pk-co">
      <Field
        label="Prospect company"
        mark
        type="search"
        placeholder="Search a company, portal or HubSpot, or type it"
        autoComplete="off"
        value={value}
        error={error}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
      />
      {shown && (
        <div className="picker pk-to-res" role="listbox" aria-label="Companies">
          {items.length ? (
            items.map((c) => (
              <button
                key={`${c.source ?? 'portal'}:${c.hubspotId ?? c.id ?? c.label}`}
                type="button"
                role="option"
                aria-selected={false}
                className="lp-it"
                onClick={() => {
                  onChange(c.label);
                  setOpen(false);
                }}
              >
                <span className="lp-k company">
                  {c.source === 'hubspot' ? 'HubSpot' : 'Portal'}
                </span>
                <span>
                  <span className="tt">{c.label}</span>
                  {c.sub && <small>{c.sub}</small>}
                </span>
              </button>
            ))
          ) : (
            <div className="lp-msg">No company found: it is kept as typed.</div>
          )}
          {hubspotDown && (
            <div className="lp-foot">HubSpot does not answer: the portal's companies only.</div>
          )}
        </div>
      )}
    </div>
  );
}

/** Send to: a contact with an email, from the portal or HubSpot (prototype: .pk-to). */
function SendTo({
  value,
  fixed,
  error,
  onChange,
}: {
  value: Recipient | null;
  fixed: boolean;
  error?: string;
  onChange(r: Recipient | null): void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Recipient[] | null>(null);
  const [hubspotDown, setHubspotDown] = useState(false);
  const inputId = useId();

  useEffect(() => {
    if (!open) return;
    let current = true;
    const timer = setTimeout(() => {
      get<{ items: Recipient[]; hubspot?: string }>(
        `/packages/recipients?q=${encodeURIComponent(q.trim())}`,
      )
        .then((r) => {
          if (!current) return;
          setItems(r.items);
          setHubspotDown(r.hubspot === 'unavailable');
        })
        .catch(() => current && setItems([]));
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [q, open]);

  const domain = value?.email.split('@')[1] ?? '';
  const personal = isPersonalDomain(domain);

  return (
    <div className="pk-to">
      <span className="fm-l">
        <label htmlFor={inputId}>
          Send to{' '}
          <span className="req" aria-hidden="true">
            *
          </span>
        </label>
      </span>
      <small className="note">
        The contact who receives the link. Access is limited to their company email domain, or to
        this exact email if it is a personal address.
      </small>
      {value ? (
        <div className="pk-to-card">
          <span className="lp-k contact">{value.source === 'hubspot' ? 'HubSpot' : 'Portal'}</span>
          <span className="pk-to-tx">
            <b>{value.name}</b>
            <span>
              {value.email}
              {value.company ? `, ${value.company}` : ''}
            </span>
            <small className="note">
              {personal
                ? 'Opens only with this exact email (personal address)'
                : `Opens with any @${domain} email`}
            </small>
          </span>
          {!fixed && (
            <button
              type="button"
              className="x"
              aria-label="Change contact"
              onClick={() => onChange(null)}
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <>
          <input
            id={inputId}
            type="search"
            placeholder="Search a contact, portal or HubSpot"
            autoComplete="off"
            aria-invalid={error ? true : undefined}
            value={q}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
          />
          {open && items && (
            <div className="picker pk-to-res" role="listbox" aria-label="Contacts">
              {items.length ? (
                items.map((r) => (
                  <button
                    key={`${r.source}:${r.id}`}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="lp-it"
                    onClick={() => {
                      onChange(r);
                      setOpen(false);
                      setQ('');
                    }}
                  >
                    <span className="lp-k contact">
                      {r.source === 'hubspot' ? 'HubSpot' : 'Portal'}
                    </span>
                    <span>
                      <span className="tt">{r.name}</span>
                      <small>
                        {r.email}
                        {r.company ? `, ${r.company}` : ''}
                      </small>
                    </span>
                  </button>
                ))
              ) : (
                <div className="lp-msg">No contact with an email found.</div>
              )}
              {hubspotDown && (
                <div className="lp-foot">HubSpot does not answer: the portal's contacts only.</div>
              )}
            </div>
          )}
          {error && <small className="err">{error}</small>}
        </>
      )}
    </div>
  );
}
